import type {
  PublicEventResponse,
  RegisterRequest,
  RegisterResponse,
  AdminMeResponse,
  AdminEventsListResponse,
  CreateEventRequest,
  CreateEventResponse,
  AdminEventDetailResponse,
  PatchEventRequest,
} from '@shared/types';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Kein Perf-Beacon mehr. Er hat die Dauer jedes Admin-Requests gemessen und
  // langsame Faelle nach `/api/admin/perflog` gemeldet — gebaut gegen die
  // D1-Stalls von Cloudflare. Der Dienst spricht seit dem Hetzner-Umzug
  // lokales SQLite; die Fehlerklasse gibt es dort nicht.
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const json = (await res.json()) as { error?: string };
      if (json?.error) code = json.error;
    } catch { /* non-JSON body */ }
    throw new ApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // Public
  getPublicEvent: (slug: string) =>
    request<PublicEventResponse>(`/api/events/${encodeURIComponent(slug)}`),
  register: (slug: string, body: RegisterRequest) =>
    request<RegisterResponse>(`/api/events/${encodeURIComponent(slug)}/register`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Admin
  adminMe: () => request<AdminMeResponse>('/api/admin/me'),
  adminListEvents: () => request<AdminEventsListResponse>('/api/admin/events'),
  adminCreateEvent: (body: CreateEventRequest) =>
    request<CreateEventResponse>('/api/admin/events', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminGetEvent: (id: number) =>
    request<AdminEventDetailResponse>(`/api/admin/events/${id}`),
  adminPatchEvent: (id: number, body: PatchEventRequest) =>
    request<{ ok: true }>(`/api/admin/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  adminDeleteEvent: (id: number) =>
    request<{ ok: true }>(`/api/admin/events/${id}`, { method: 'DELETE' }),
  adminDeleteRegistrationGroup: (eventId: number, groupId: string) =>
    request<{ ok: true; removed: number }>(
      `/api/admin/events/${eventId}/groups/${encodeURIComponent(groupId)}`,
      { method: 'DELETE' },
    ),
  adminExportCsvUrl: (id: number) => `/api/admin/events/${id}/export.csv`,
};
